import { Component, OnInit, ViewChild, Renderer2, Inject, Input } from '@angular/core';
import { UsersService } from 'src/app/service/users/users.service';
import { RolesService } from 'src/app/service/roles/roles.service';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ExportToCsv } from 'export-to-csv';
import { LocalstorageService } from 'src/app/service/shared/localstorage/localstorage.service';
import { ToolSettingsService } from 'src/app/service/tool-settings/tool-settings.service';
import { JobPointeSettingsService } from 'src/app/service/job-pointe-settings/job-pointe-settings.service';
import { PermissionsService } from 'src/app/service/auth/permissions.service';
import { boolToInt } from 'src/app/utils/boolToInt';
import { setFormBuilderPermissionFields } from 'src/app/helpers/setFormBuilderPermissionFields';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import getAndParseUserPermissions from 'src/app/helpers/parsePermissions';

const viewAccess = { view: [1], edit: [0] }
const noPermissions = { view: [0], edit: [0] };

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit {
  @ViewChild('openModel') openModel;
  @ViewChild('closebutton') closebutton;
  @ViewChild('closebutton1') closebutton1;
  @ViewChild('closebutton2') closebutton2;

  private searchInput: Subject<string> = new Subject<string>();
  usersData = [];
  userNames = {};
  rolesData = [];
  noDataMsg = '';
  mode = 'Add';
  title = '';
  deleteUserId;
  searchInfo = {
    hub_access: 'true',
  };
  statusUserId;
  userStatus;
  activeCount: 0;
  inActiveCount: 0;
  inSpecialPermission: 0;
  userPermissions;
  @Input() system_users_setting: any;
  assetClasses: any;
  userForm: FormGroup;

  constructor(
    private usersService: UsersService,
    private rolesService: RolesService,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    // Still needed in order for getAndParseUserPermissions to work.
    private localStorageService: LocalstorageService,
    private toolSettingsService: ToolSettingsService,
    private jobPointeSettingsService: JobPointeSettingsService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private permissionsService: PermissionsService,
  ) {
    this.createPermissionFormIfDataExists = this.createPermissionFormIfDataExists.bind(this);
    this.onDeleteError = this.onDeleteError.bind(this);
    this.onDeleteSuccess = this.onDeleteSuccess.bind(this);
    this.onError = this.onError.bind(this);
    this.onRolesError = this.onRolesError.bind(this);
    this.onRolesSuccess = this.onRolesSuccess.bind(this);
    this.onSubmitError = this.onSubmitSuccess.bind(this);
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
    this.onUserGetError = this.onUserGetError.bind(this);
    this.onUserGetSuccess = this.onUserGetSuccess.bind(this);
  }

  ngOnInit(): void {
    this.getActiveAssetClasses();
    this.getUsers();
    this.getRoles();
    this.createForm();
    this.userPermissions = getAndParseUserPermissions(this.localStorageService)

    this.searchInput.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.searchInTable(value, 'search');
    });
  }

  clearDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `clearDatatable('.user-data-table')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  drawDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `reloadDataTable('.user-data-table')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  createPermissionFormIfDataExists = data => {
    const permissions = JSON.parse(data.permissions);

    const excludedRoles = ['tools', 'tools_history', 'tool_models', 'asset_class'];
    const rolesArr = Object.keys(permissions).filter(key => !excludedRoles.includes(key.toLowerCase())) ?? [];

    const permissionsObj = {};

    rolesArr.map(key => {
      permissionsObj[key] = this.formBuilder.group(setFormBuilderPermissionFields(permissions, key));
    });

    this.title = data.title;
    this.userForm = this.formBuilder.group({
      first_name: [data.first_name, Validators.required],
      last_name: [data.last_name, Validators.required],
      employee_alias: [data.employee_alias],
      email: [data.email, [Validators.required, Validators.email]],
      password: [],
      title: [data.title, Validators.required],
      phone: [data.phone],
      role_id: [data.role_id, Validators.required],
      status: [data.status, Validators.required],
      id: [data.id],
      pp_access: [data.pp_access == 'True' || data.pp_access == 1 ? 1 : 0],
      permissions: this.formBuilder.group({
        receiving: this.formBuilder.group(noPermissions),
        receiving_module_po_update_notify_owner: this.formBuilder.group(noPermissions),
        ...permissionsObj,
        invoicing_reset: this.formBuilder.group({
          edit: [boolToInt(permissions.invoicing_reset.edit)],
        }),

        // Tools Permissions
        tools: this.formBuilder.group({
          view: [boolToInt(permissions.tools.view)],
          detailed_view: [boolToInt(permissions.tools.detailed_view)],
          edit: [boolToInt(permissions.tools.edit)],
          admin: [boolToInt(permissions.tools.admin)],
        }),
        tools_history: this.formBuilder.group({
          view: [boolToInt(permissions.tools_history.view)],
          detailed_view: [boolToInt(permissions.tools_history.detailed_view)],
        }),
        tool_inventory: this.formBuilder.group({
          view: [boolToInt(permissions.tool_inventory.view)],
          edit: [boolToInt(permissions.tool_inventory.edit)]
        }),
        tool_models: this.formBuilder.group({
          view: [boolToInt(permissions.tool_models.view)],
          detailed_view: [boolToInt(permissions.tool_models.detailed_view)],
          edit: [boolToInt(permissions.tool_models.view)],
          admin: [boolToInt(permissions.tool_models.edit)],
        }),
        asset_class: this.formBuilder.group({}),
      }),
      updated_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
    });
    this.getActiveAssetClasses(permissions);
  };

  createForm(data = null) {
    this.getRoles();
    if (!!data) {
      this.createPermissionFormIfDataExists(data);
      return;
    }

    this.userForm = this.formBuilder.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      employee_alias: [''],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      title: ['', Validators.required],
      phone: [''],
      role_id: ['', Validators.required],
      status: ['', Validators.required],
      id: [''],
      pp_access: [false],
      permissions: this.formBuilder.group({
        main_job_list: this.formBuilder.group(viewAccess),
        general_info: this.formBuilder.group(viewAccess),
        billing_accounting: this.formBuilder.group(viewAccess),
        financials: this.formBuilder.group(noPermissions),
        documents: this.formBuilder.group(noPermissions),
        audit_trail: this.formBuilder.group(noPermissions),
        locked_fields: this.formBuilder.group(noPermissions),
        working_status: this.formBuilder.group(noPermissions),
        invoicing_list: this.formBuilder.group(viewAccess),
        invoicing_reset: this.formBuilder.group({ edit: [0] }),
        job_status: this.formBuilder.group(viewAccess),
        owner_info: this.formBuilder.group(noPermissions),
        customers: this.formBuilder.group(noPermissions),
        purchase_orders: this.formBuilder.group(noPermissions),
        po_amount: this.formBuilder.group(noPermissions),
        material_request: this.formBuilder.group(noPermissions),
        receiving: this.formBuilder.group(noPermissions),
        receiving_module_po_update_notify_owner: this.formBuilder.group(noPermissions),
        accounting: this.formBuilder.group(noPermissions),
        sage_export: this.formBuilder.group(noPermissions),
        vendors: this.formBuilder.group(noPermissions),
        roles_setting: this.formBuilder.group(noPermissions),
        system_users_setting: this.formBuilder.group(noPermissions),
        union_locals_setting: this.formBuilder.group(noPermissions),
        tools_setting: this.formBuilder.group(noPermissions),
        design_setting: this.formBuilder.group(noPermissions),
        smtp_setting: this.formBuilder.group(noPermissions),
        jobs: this.formBuilder.group(noPermissions),
        master_list: this.formBuilder.group(noPermissions),
        settings: this.formBuilder.group(noPermissions),

        // Tools Permissions
        tools: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
          edit: [0],
          admin: [0],
        }),
        tools_history: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
        }),
        tool_inventory: this.formBuilder.group(noPermissions),
        tool_models: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
          edit: [0],
          admin: [0],
        }),
        asset_class: this.formBuilder.group({}),

        // Job Pointe Permissions
        job_pointe: this.formBuilder.group(noPermissions),
        job_pointe_configuration: this.formBuilder.group(noPermissions),
        job_pointe_employees: this.formBuilder.group(noPermissions),
        job_board_attributes: this.formBuilder.group(noPermissions),
        job_board_schedules: this.formBuilder.group(noPermissions),
        job_board_tags: this.formBuilder.group(noPermissions),
        job_employee_job_board_tags: this.formBuilder.group(noPermissions),
        employee_job_board_attributes: this.formBuilder.group(noPermissions),
        manage_jobs: this.formBuilder.group(noPermissions),
      }),
    });

    this.getActiveAssetClasses(undefined);
  }

  cleanData() {
    this.userForm.reset();
    this.title = '';
  }

  createAssetClassForm(permissions = null): void {
    this.assetClasses.forEach(assetClass => {
      const assetClassPermissions = permissions?.asset_class[assetClass.name]; //TODO: handle error incase assetclass permissions don't exist
      const assetClassForm = this.userForm.get('permissions').get('asset_class') as FormGroup;

      assetClassForm.addControl(
        assetClass.name,
        new FormGroup({
          view: new FormControl(assetClassPermissions ? assetClassPermissions['view'] : 0),
          detailed_view: new FormControl(assetClassPermissions ? assetClassPermissions['detailed_view'] : 0),
          edit: new FormControl(assetClassPermissions ? assetClassPermissions['edit'] : 0),
          admin: new FormControl(assetClassPermissions ? assetClassPermissions['admin'] : 0),
        }),
      );
    });
  }

  getActiveAssetClasses(permissions = null) {
    this.toolSettingsService.read['asset-classes']({
      bypassPermissions: 1,
      filter: ''
    }).subscribe(res => {
      this.assetClasses = res.data;
      this.createAssetClassForm(permissions);
    }, this.onError);
  }

  //To get all users data
  getUsers() {
    this.usersService.getUsers(this.searchInfo, true, true).subscribe(this.onSuccess, this.onError);
  }

  //To get all roles data
  getRoles() {
    this.rolesService.getRoles().subscribe(this.onRolesSuccess, this.onRolesError);
  }

  //On success of api call
  onSuccess(result) {
    if (!result) {
      this.noDataMsg = result.errors;
    }

    if (!!result?.data) {
      this.clearDatatable();
      this.usersData = result.data;
      this.userNames = this.usersData.reduce((list, user) => {
        list[user.id] = `${user.first_name} ${user.last_name}`;
        return list;
      }, {});
      this.drawDatatable();
      this.activeCount = result.data.filter(item => !!item.status).length;
      this.inActiveCount = result.data.filter(item => !item.status).length;
      this.inSpecialPermission = result.data.filter(item => !!item.is_special_permissions).length;
      this.loadSelect2();
    }
  }

  resetLocalStorage(_: unknown) { }

  //On error of api call
  onError(_: unknown) { }

  //On success of roles api call
  onRolesSuccess(result) {
    if (!!result?.data) this.rolesData = result.data;
  }

  //On error of roles api call
  onRolesError(_: unknown) { }

  async onSubmit(form) {
    if (form.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const hasPPAccess = !!form.value.pp_access ? 'True' : 'False';
    form.value.pp_access = hasPPAccess;

    if (this.mode == 'Add') {
      delete form.value.id;

      const newDate = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

      form.value.created_date = newDate;
      form.value.updated_date = newDate;
      this.usersService.createUser(form.value).subscribe(this.onSubmitSuccess, this.onSubmitError);
    } else if (this.mode == 'Edit') {
      if (!form?.value?.password) {
        delete form.value.password;
      }

      this.usersService.updateUser(form.value.id, form.value).subscribe(this.onSubmitSuccess, this.onSubmitError);
    }
  }

  //On success of api call
  onSubmitSuccess(result) {
    if (result) {
      if (result.data) {
        this.permissionsService.refresh();
        this.closebutton.nativeElement.click();
        setTimeout(() => {
          this.getUsers();
        }, 50);
        this.closebutton2.nativeElement.click();
        if (this.mode == 'Edit') {
          this.toastService.showToast('User updated successfully', 'success');
        } else {
          this.toastService.showToast('User added successfully', 'success');
        }
        this.cleanData();
        this.title = '';
      } else {
        this.noDataMsg = result.errors;
        if (result.errors) {
          this.toastService.showToast(result.errors, 'error');
        }
      }
    }
  }

  //On error of api call
  onSubmitError(_: unknown) { }

  setMode(mode, id = '') {
    this.mode = mode;
    this.cleanData();

    if (!!id) {
      this.usersService.getUsersById(id, true, true).subscribe(this.onUserGetSuccess, this.onUserGetError);
    }
  }

  //On success of api call
  onUserGetSuccess(result) {
    if (!result.data) {
      this.noDataMsg = result.errors;
    } else if (!!result?.data) {
      this.createForm(result.data);
    }
  }

  //On error of api call
  onUserGetError(_: unknown) { }

  //Delete user based on id
  deleteUser(id: string) {
    if (!!id) {
      this.usersService.deleteUser(id).subscribe(this.onDeleteSuccess, this.onDeleteError);
    }
  }

  //Set user id to delete on confirm delete modal
  setDeleteUserId(id) {
    this.deleteUserId = id;
  }

  //On success of api call
  onDeleteSuccess(result) {
    if (result) {
      if (result.data) {
        this.closebutton1.nativeElement.click();
        this.toastService.showToast(result.data.message, 'success');
        this.getUsers();
      }
    }
  }

  //On error of api call
  onDeleteError(_: unknown) { }

  onOptionsSelected(role_id = null) {
    if (this.mode !== 'Edit') {
      const role = this.rolesData.find(item => item.id === role_id);
      this.title = role.role_name;
      this.userForm.controls['title'].setValue(this.title);
    }

    if (role_id) this.setNewRolePermission(role_id);
  }

  setNewRolePermission(role_id) {
    if (role_id != '') {
      let selectedRole = this.rolesData.filter(function (item) {
        return item.id == role_id;
      });
      if (selectedRole) {
        let permissions = JSON.parse(selectedRole[0].permissions);
        this.userForm
          .get(['permissions', 'main_job_list', 'view'])
          .setValue(permissions.main_job_list ? permissions.main_job_list['view'] : 0);
        this.userForm
          .get(['permissions', 'general_info', 'view'])
          .setValue(permissions.general_info ? permissions.general_info['view'] : 0);
        this.userForm
          .get(['permissions', 'billing_accounting', 'view'])
          .setValue(permissions.billing_accounting ? permissions.billing_accounting['view'] : 0);
        this.userForm
          .get(['permissions', 'financials', 'view'])
          .setValue(permissions.financials ? permissions.financials['view'] : 0);
        this.userForm
          .get(['permissions', 'documents', 'view'])
          .setValue(permissions.documents ? permissions.documents['view'] : 0);
        this.userForm
          .get(['permissions', 'audit_trail', 'view'])
          .setValue(permissions.audit_trail ? permissions.audit_trail['view'] : 0);
        this.userForm
          .get(['permissions', 'locked_fields', 'view'])
          .setValue(permissions.locked_fields ? permissions.locked_fields['view'] : 0);
        this.userForm
          .get(['permissions', 'invoicing_list', 'view'])
          .setValue(permissions.invoicing_list ? permissions.invoicing_list['view'] : 0);
        this.userForm
          .get(['permissions', 'job_status', 'view'])
          .setValue(permissions.job_status ? permissions.job_status['view'] : 0);
        this.userForm
          .get(['permissions', 'owner_info', 'view'])
          .setValue(permissions.owner_info ? permissions.owner_info['view'] : 0);
        this.userForm
          .get(['permissions', 'customers', 'view'])
          .setValue(permissions.customers ? permissions.customers['view'] : 0);
        this.userForm
          .get(['permissions', 'purchase_orders', 'view'])
          .setValue(permissions.purchase_orders ? permissions.purchase_orders['view'] : 0);
        this.userForm
          .get(['permissions', 'po_amount', 'view'])
          .setValue(permissions.po_amount ? permissions.po_amount['view'] : 0);
        this.userForm
          .get(['permissions', 'material_request', 'view'])
          .setValue(permissions.material_request ? permissions.material_request['view'] : 0);
        this.userForm
          .get(['permissions', 'accounting', 'view'])
          .setValue(permissions.accounting ? permissions.accounting['view'] : 0);
        this.userForm
          .get(['permissions', 'sage_export', 'view'])
          .setValue(permissions.sage_export ? permissions.sage_export['view'] : 0);
        this.userForm
          .get(['permissions', 'vendors', 'view'])
          .setValue(permissions.vendors ? permissions.vendors['view'] : 0);
        this.userForm
          .get(['permissions', 'working_status', 'view'])
          .setValue(permissions.working_status ? permissions.working_status['view'] : 0);
        this.userForm
          .get(['permissions', 'roles_setting', 'view'])
          .setValue(permissions.roles_setting ? permissions.roles_setting['view'] : 0);
        this.userForm
          .get(['permissions', 'system_users_setting', 'view'])
          .setValue(permissions.system_users_setting ? permissions.system_users_setting['view'] : 0);
        this.userForm.get(['permissions', 'receiving_module_po_update_notify_owner', 'view'])
          .setValue(permissions.receiving_module_po_update_notify_owner ? permissions.receiving_module_po_update_notify_owner['view'] : 0)
        this.userForm
          .get(['permissions', 'union_locals_setting', 'view'])
          .setValue(permissions.union_locals_setting ? permissions.union_locals_setting['view'] : 0);
        this.userForm
          .get(['permissions', 'tools_setting', 'view'])
          .setValue(permissions.tools_setting ? permissions.tools_setting['view'] : 0);
        this.userForm
          .get(['permissions', 'design_setting', 'view'])
          .setValue(permissions.design_setting ? permissions.design_setting['view'] : 0);
        this.userForm
          .get(['permissions', 'smtp_setting', 'view'])
          .setValue(permissions.smtp_setting ? permissions.smtp_setting['view'] : 0);
        this.userForm.get(['permissions', 'jobs', 'view']).setValue(permissions.jobs ? permissions.jobs['view'] : 0);
        this.userForm
          .get(['permissions', 'master_list', 'view'])
          .setValue(permissions.master_list ? permissions.master_list['view'] : 0);
        this.userForm
          .get(['permissions', 'settings', 'view'])
          .setValue(permissions.settings ? permissions.settings['view'] : 0);

        this.userForm
          .get(['permissions', 'main_job_list', 'edit'])
          .setValue(permissions.main_job_list ? permissions.main_job_list['edit'] : 0);
        this.userForm
          .get(['permissions', 'general_info', 'edit'])
          .setValue(permissions.general_info ? permissions.general_info['edit'] : 0);
        this.userForm
          .get(['permissions', 'billing_accounting', 'edit'])
          .setValue(permissions.billing_accounting ? permissions.billing_accounting['edit'] : 0);
        this.userForm
          .get(['permissions', 'financials', 'edit'])
          .setValue(permissions.financials ? permissions.financials['edit'] : 0);
        this.userForm
          .get(['permissions', 'documents', 'edit'])
          .setValue(permissions.documents ? permissions.documents['edit'] : 0);
        this.userForm
          .get(['permissions', 'audit_trail', 'edit'])
          .setValue(permissions.audit_trail ? permissions.audit_trail['edit'] : 0);
        this.userForm
          .get(['permissions', 'locked_fields', 'edit'])
          .setValue(permissions.locked_fields ? permissions.locked_fields['edit'] : 0);
        this.userForm
          .get(['permissions', 'invoicing_list', 'edit'])
          .setValue(permissions.invoicing_list ? permissions.invoicing_list['edit'] : 0);
        this.userForm
          .get(['permissions', 'invoicing_reset', 'edit'])
          .setValue(permissions.invoicing_reset ? permissions.invoicing_reset['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_status', 'edit'])
          .setValue(permissions.job_status ? permissions.job_status['edit'] : 0);
        this.userForm
          .get(['permissions', 'owner_info', 'edit'])
          .setValue(permissions.owner_info ? permissions.owner_info['edit'] : 0);
        this.userForm
          .get(['permissions', 'customers', 'edit'])
          .setValue(permissions.customers ? permissions.customers['edit'] : 0);
        this.userForm
          .get(['permissions', 'purchase_orders', 'edit'])
          .setValue(permissions.purchase_orders ? permissions.purchase_orders['edit'] : 0);
        this.userForm
          .get(['permissions', 'po_amount', 'edit'])
          .setValue(permissions.po_amount ? permissions.po_amount['edit'] : 0);
        this.userForm
          .get(['permissions', 'material_request', 'edit'])
          .setValue(permissions.material_request ? permissions.material_request['edit'] : 0);
        this.userForm
          .get(['permissions', 'accounting', 'edit'])
          .setValue(permissions.accounting ? permissions.accounting['edit'] : 0);
        this.userForm
          .get(['permissions', 'sage_export', 'edit'])
          .setValue(permissions.sage_export ? permissions.sage_export['edit'] : 0);
        this.userForm
          .get(['permissions', 'vendors', 'edit'])
          .setValue(permissions.vendors ? permissions.vendors['edit'] : 0);
        this.userForm
          .get(['permissions', 'working_status', 'edit'])
          .setValue(permissions.working_status ? permissions.working_status['edit'] : 0);
        this.userForm
          .get(['permissions', 'roles_setting', 'edit'])
          .setValue(permissions.roles_setting ? permissions.roles_setting['edit'] : 0);
        this.userForm
          .get(['permissions', 'system_users_setting', 'edit'])
          .setValue(permissions.system_users_setting ? permissions.system_users_setting['edit'] : 0);
        this.userForm
          .get(['permissions', 'union_locals_setting', 'edit'])
          .setValue(permissions.union_locals_setting ? permissions.union_locals_setting['edit'] : 0);
        this.userForm
          .get(['permissions', 'tools_setting', 'edit'])
          .setValue(permissions.tools_setting ? permissions.tools_setting['edit'] : 0);
        this.userForm
          .get(['permissions', 'design_setting', 'edit'])
          .setValue(permissions.design_setting ? permissions.design_setting['edit'] : 0);
        this.userForm
          .get(['permissions', 'smtp_setting', 'edit'])
          .setValue(permissions.smtp_setting ? permissions.smtp_setting['edit'] : 0);
        this.userForm.get(['permissions', 'jobs', 'edit']).setValue(permissions.jobs ? permissions.jobs['edit'] : 0);
        this.userForm
          .get(['permissions', 'master_list', 'edit'])
          .setValue(permissions.master_list ? permissions.master_list['edit'] : 0);
        this.userForm
          .get(['permissions', 'settings', 'edit'])
          .setValue(permissions.settings ? permissions.settings['edit'] : 0);

        // Tools Permissions
        this.userForm.get(['permissions', 'tools', 'view']).setValue(permissions.tools ? permissions.tools['view'] : 0);
        this.userForm
          .get(['permissions', 'tools', 'detailed_view'])
          .setValue(permissions.tools ? permissions.tools['detailed_view'] : 0);
        this.userForm.get(['permissions', 'tools', 'edit']).setValue(permissions.tools ? permissions.tools['edit'] : 0);
        this.userForm
          .get(['permissions', 'tools', 'admin'])
          .setValue(permissions.tools ? permissions.tools['admin'] : 0);

        this.userForm
          .get(['permissions', 'tools_history', 'view'])
          .setValue(permissions.tools ? permissions.tools['view'] : 0);
        this.userForm
          .get(['permissions', 'tools_history', 'detailed_view'])
          .setValue(permissions.tools ? permissions.tools['detailed_view'] : 0);

        this.userForm
          .get(['permissions', 'tool_inventory', 'view'])
          .setValue(permissions.tool_inventory ? permissions.tool_inventory['view'] : 0);
        this.userForm
          .get(['permissions', 'tool_inventory', 'edit'])
          .setValue(permissions.tool_inventory ? permissions.tool_inventory['edit'] : 0);

        this.userForm
          .get(['permissions', 'tool_models', 'view'])
          .setValue(permissions.tool_models ? permissions.tool_models['view'] : 0);
        this.userForm
          .get(['permissions', 'tool_models', 'detailed_view'])
          .setValue(permissions.tool_models ? permissions.tool_models['detailed_view'] : 0);
        this.userForm
          .get(['permissions', 'tool_models', 'edit'])
          .setValue(permissions.tool_models ? permissions.tool_models['edit'] : 0);
        this.userForm
          .get(['permissions', 'tool_models', 'admin'])
          .setValue(permissions.tool_models ? permissions.tool_models['admin'] : 0);

        if (permissions.asset_class && Object.keys(permissions.asset_class).length) {
          Object.keys(permissions.asset_class).forEach(assetClass => {
            this.userForm
              .get(['permissions', 'asset_class', assetClass, 'view'])
              .setValue(permissions.asset_class[assetClass] ? permissions.asset_class[assetClass]['view'] : 0);
            this.userForm
              .get(['permissions', 'asset_class', assetClass, 'detailed_view'])
              .setValue(permissions.asset_class[assetClass] ? permissions.asset_class[assetClass]['detailed_view'] : 0);
            this.userForm
              .get(['permissions', 'asset_class', assetClass, 'edit'])
              .setValue(permissions.asset_class[assetClass] ? permissions.asset_class[assetClass]['edit'] : 0);
            this.userForm
              .get(['permissions', 'asset_class', assetClass, 'admin'])
              .setValue(permissions.asset_class[assetClass] ? permissions.asset_class[assetClass]['admin'] : 0);
          });
        }

        // Job Pointe Permissions
        this.userForm
          .get(['permissions', 'job_pointe', 'view'])
          .setValue(permissions.job_pointe ? permissions.job_pointe['view'] : 0);
        this.userForm
          .get(['permissions', 'job_pointe', 'edit'])
          .setValue(permissions.job_pointe ? permissions.job_pointe['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_pointe_configuration', 'view'])
          .setValue(permissions.job_pointe_configuration ? permissions.job_pointe_configuration['view'] : 0);
        this.userForm
          .get(['permissions', 'job_pointe_configuration', 'edit'])
          .setValue(permissions.job_pointe_configuration ? permissions.job_pointe_configuration['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_pointe_employees', 'view'])
          .setValue(permissions.job_pointe_employees ? permissions.job_pointe_employees['view'] : 0);
        this.userForm
          .get(['permissions', 'job_pointe_employees', 'edit'])
          .setValue(permissions.job_pointe_employees ? permissions.job_pointe_employees['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_board_attributes', 'view'])
          .setValue(permissions.job_board_attributes ? permissions.job_board_attributes['view'] : 0);
        this.userForm
          .get(['permissions', 'job_board_attributes', 'edit'])
          .setValue(permissions.job_board_attributes ? permissions.job_board_attributes['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_board_schedules', 'view'])
          .setValue(permissions.job_board_schedules ? permissions.job_board_schedules['view'] : 0);
        this.userForm
          .get(['permissions', 'job_board_schedules', 'edit'])
          .setValue(permissions.job_board_schedules ? permissions.job_board_schedules['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_board_tags', 'view'])
          .setValue(permissions.job_board_tags ? permissions.job_board_tags['view'] : 0);
        this.userForm
          .get(['permissions', 'job_board_tags', 'edit'])
          .setValue(permissions.job_board_tags ? permissions.job_board_tags['edit'] : 0);
        this.userForm
          .get(['permissions', 'job_employee_job_board_tags', 'view'])
          .setValue(permissions.job_employee_job_board_tags ? permissions.job_employee_job_board_tags['view'] : 0);
        this.userForm
          .get(['permissions', 'job_employee_job_board_tags', 'edit'])
          .setValue(permissions.job_employee_job_board_tags ? permissions.job_employee_job_board_tags['edit'] : 0);
        this.userForm
          .get(['permissions', 'employee_job_board_attributes', 'view'])
          .setValue(permissions.employee_job_board_attributes ? permissions.employee_job_board_attributes['view'] : 0);
        this.userForm
          .get(['permissions', 'employee_job_board_attributes', 'edit'])
          .setValue(permissions.employee_job_board_attributes ? permissions.employee_job_board_attributes['edit'] : 0);
        this.userForm
          .get(['permissions', 'manage_jobs', 'view'])
          .setValue(permissions.manage_jobs ? permissions.manage_jobs['view'] : 0);
        this.userForm
          .get(['permissions', 'manage_jobs', 'edit'])
          .setValue(permissions.manage_jobs ? permissions.manage_jobs['edit'] : 0);
        this.userForm
          .get(['permissions', 'configuration', 'view'])
          .setValue(permissions.configuration ? permissions.configuration['view'] : 0);
        this.userForm
          .get(['permissions', 'configuration', 'edit'])
          .setValue(permissions.configuration ? permissions.configuration['edit'] : 0);
      }
    }
  }

  handleSearch(value: string): void {
    this.searchInput.next(value);
  }

  searchInTable(value, key) {
    if (value === '') {
      delete this.searchInfo[key];
    } else {
      this.searchInfo[key] = value;
    }
    this.getUsers();
  }

  changeStatus(id, status) {
    if (id != '') {
      let userData1 = {
        id: id,
        status: status,
      };
      this.usersService.updateUser(id, userData1).subscribe(this.onSubmitSuccess, this.onSubmitError);
    }
  }

  setUserStatusId(event, id) {
    this.statusUserId = id;
    this.userStatus = event.target.checked ? 1 : 0;
  }

  //Set 0/1 on checkbox checked false/true
  setValue(event, formGroupName, key) {
    const checked = !!event.target.checked;
    const checkedValue = boolToInt(checked);

    if (key === 'view') {
      if (!checked) {
        this.userForm.get(['permissions', formGroupName, 'edit'])?.setValue(0);
        this.userForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(0);
        this.userForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else if (key === 'detailed_view') {
      if (checked) {
        this.userForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
      } else {
        this.userForm.get(['permissions', formGroupName, 'edit'])?.setValue(0);
        this.userForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else if (key === 'edit') {
      if (checked) {
        this.userForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
        this.userForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(1);
      } else {
        if (formGroupName === 'receiving') {
          this.userForm.get(['permissions', formGroupName, 'view'])?.setValue(0);
        }
        this.userForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else {
      if (checked) {
        this.userForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
        this.userForm.get(['permissions', formGroupName, 'edit'])?.setValue(1);
        this.userForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(1);
      }
    }
    this.userForm.get(['permissions', formGroupName, key]).setValue(checkedValue);
  }

  //Set 0/1 on checkbox checked false/true
  setAssetClassValue(event, formGroupName, key: 'view' | 'edit' | 'detailed_view' | 'admin') {
    const checked: boolean = event?.target?.checked || false;
    const assetClassPermissionsFormGroupSearch = ['permissions', 'asset_class', formGroupName];

    this.userForm.get([...assetClassPermissionsFormGroupSearch, key])?.setValue(boolToInt(checked));

    if (key === 'view') {
      if (!checked) {
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(0);
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(0);
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    } else if (key === 'detailed_view') {
      if (checked) {
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
      } else {
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(0);
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    } else if (key === 'edit') {
      if (checked) {
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(1);
      } else {
        this.userForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    }

    if (checked) {
      this.userForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
      this.userForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(1);
      this.userForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(1);
    }
  }

  getAssetClasses = () => this.userForm.get('permissions').get('asset_class')['controls'];

  generateCSV() {
    const options = {
      filename: 'User Data Export CSV',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      // showTitle: true,
      // title: 'Users Data Export CSV',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
    };

    const csvExporter = new ExportToCsv(options);

    const userExport = [];

    this.usersData.forEach(element => {
      const newData = {
        Id: element.id,
        'Role Name': element.role_name,
        'Employee Id': element.employee_id,
        'First Name': element.first_name,
        'Last Name': element.last_name,
        Email: element.email,
        Title: element.title,
        'Special Permissions': element.is_special_permissions ? 'Yes' : '',
        'Phone No': element.phone,
        'PP Access': element.pp_access,
        Alias: element.employee_alias,
      };
      userExport.push(newData);
    });

    csvExporter.generateCsv(userExport);
  }

  setDataToControl = (field, value) => this.userForm.get(field).setValue(value);
}
